import { createContext, ReactNode, useContext, useMemo } from 'react'
import { Permission, PermissionValue, usePermissions } from '../api/usePermissions'

interface PermissionsContextValue {
  permissions: PermissionValue[]
  roles: string[]
  isLoading: boolean
  hasPermission: (permission: PermissionValue) => boolean
  hasAnyPermission: (permissions: PermissionValue[]) => boolean
  hasAllPermissions: (permissions: PermissionValue[]) => boolean
  canAccessSubmerchants: boolean
  canAccessMonthlyBilling: boolean
  canAccessShareRules: boolean
  canAccessVoucherCodes: boolean
}

const PermissionsContext = createContext<PermissionsContextValue | null>(null)

interface PermissionsProviderProps {
  children: ReactNode
}

export function PermissionsProvider({ children }: PermissionsProviderProps) {
  const { data, isLoading, isFetching } = usePermissions()
  const isPermissionsReady = !isLoading && !isFetching && !!data

  const value = useMemo<PermissionsContextValue>(() => {
    const permissions = data?.permissions || []
    const roles = data?.roles || []

    function hasPermission(permission: PermissionValue): boolean {
      return permissions.includes(permission)
    }

    function hasAnyPermission(requiredPermissions: PermissionValue[]): boolean {
      return requiredPermissions.some((permission) => permissions.includes(permission))
    }

    function hasAllPermissions(requiredPermissions: PermissionValue[]): boolean {
      return requiredPermissions.every((permission) => permissions.includes(permission))
    }

    return {
      permissions,
      roles,
      isLoading: !isPermissionsReady,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      canAccessSubmerchants: hasPermission(Permission.SUBMERCHANTS_READ),
      canAccessMonthlyBilling: hasPermission(Permission.MONTHLY_BILLING_READ),
      canAccessShareRules: hasPermission(Permission.SHARE_RULES_READ),
      canAccessVoucherCodes: hasPermission(Permission.VOUCHER_CODES_READ)
    }
  }, [data, isPermissionsReady])

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  )
}

export function usePermissionsContext(): PermissionsContextValue {
  const context = useContext(PermissionsContext)
  if (!context) {
    throw new Error('usePermissionsContext must be used within a PermissionsProvider')
  }

  return context
}
